import { requiresExplicitConsent } from "./consentPurpose";
/**
 * Badge theme per status. Shared with the consents tab, which imports it rather than keeping its
 * own copy so the two surfaces cannot render the same consent in two different colours.
 *
 * All three statuses, because the tab renders refusals; this widget renders only the two in
 * {@link DisplayedConsentStatus}. The values are what the design asks for — opted-in purposes
 * resolve to `badge-green-bg` (#d0fae5) and unspecified ones to `badge-neutral-bg` (#e5e5e5).
 * `red` for a refusal has no design behind it and is carried over from the tab, the only
 * precedent; it is reachable from the tab alone.
 */
export const CONSENT_STATUS_THEME = {
    NotSet: "neutral",
    OptedIn: "green",
    OptedOut: "red",
};
/**
 * Status glyph for all three statuses — a status is never conveyed by colour alone (RGAA), so
 * every rendered badge and every dropdown option pairs its theme with one of these.
 *
 * All three come from the design's own icon components, identified from the exported SVG geometry
 * rather than guessed from a render.
 */
export const CONSENT_STATUS_ICON = {
    NotSet: "ri-question-line",
    OptedIn: "ri-checkbox-circle-line",
    OptedOut: "ri-close-circle-line",
};
/**
 * The same table, narrowed to what a badge line may render.
 *
 * An alias rather than a second literal — the glyphs cannot drift apart — but a distinct type, so
 * indexing it with `OptedOut` stays a compile error and a badge line cannot quietly widen to
 * render refusals.
 */
export const DISPLAYED_STATUS_ICON = CONSENT_STATUS_ICON;
/**
 * Icon colour per status, as the design's "Finalités" dropdowns draw them — green for a granted
 * purpose, neutral grey for one never asked, red for a refusal (Figma `Icons/*`).
 */
export const CONSENT_STATUS_ICON_COLOR = {
    NotSet: "base",
    OptedIn: "success",
    OptedOut: "error",
};
/**
 * The purposes listed for one address: its channel's enabled matrix purposes, plus every purpose
 * that already carries a decision, so a purpose deactivated after an opt-in stays revocable.
 * A purpose that requires explicit consent (tracking) is the exception: it is listed only while its
 * matrix cell is enabled, because disabling that cell is how a workspace turns it off altogether.
 */
export function listedPurposeCodes(activePurposes, decidedPurposes, kindOf) {
    const active = new Set(activePurposes);
    const decided = [...decidedPurposes].filter((code) => active.has(code) || !requiresExplicitConsent(kindOf(code)));
    return Array.from(new Set([...activePurposes, ...decided]));
}
/**
 * Badge order within a line: what the contact agreed to first, what was never asked after. A badge
 * line answers "what may we send to this address?" at a glance, so the granted purposes lead and
 * the open ones follow — and when the line overflows, it is the never-asked ones that fall into
 * the `+N` popover rather than the granted ones.
 *
 * Keyed on {@link DisplayedConsentStatus} so a new displayed status has to declare its rank
 * rather than silently landing wherever the source data happened to put it.
 */
const DISPLAYED_STATUS_ORDER = {
    OptedIn: 0,
    NotSet: 1,
};
/**
 * The badge-line display rule, in one place: drop the refusals, then rank granted purposes ahead
 * of never-asked ones.
 *
 * Shared by the record-view widget and the consents tab so the same address cannot be summarised
 * two different ways on two screens — the tab used to render all three statuses in source order,
 * which put a refusal on screen and let a granted purpose be the one hidden behind `+N`.
 *
 * Takes the tab's wider {@link ConsentPurposeVm} and returns the narrowed
 * {@link ConsentPurposeBadgeVm}: the filter IS the narrowing, so a caller cannot forget it and
 * still typecheck. Note this is display-only — the edit modal keeps the unfiltered list, or a
 * refusal would become impossible to undo.
 *
 * `sort` mutates, but only the array `filter` just allocated; the caller's array is untouched. The
 * sort is stable (ES2019), so within a status the caller's own order is preserved — the ranking
 * regroups, it does not reshuffle.
 */
export function toDisplayedPurposes(purposes) {
    // A purpose requiring explicit consent shows only once granted; the edit modal still lists it as NotSet.
    return purposes
        .filter((purpose) => purpose.status !== "OptedOut" && !(requiresExplicitConsent(purpose.kind) && purpose.status === "NotSet"))
        .sort((a, b) => DISPLAYED_STATUS_ORDER[a.status] - DISPLAYED_STATUS_ORDER[b.status]);
}
/**
 * Channel icon per address type. Lives here rather than inline in the card because the consents
 * tab renders the same mapping off the same data, and the tab's redesign should be able to import
 * it instead of re-deriving it.
 */
export const CONSENT_ADDRESS_TYPE_ICON = {
    Email: "ri-mail-line",
    Phone: "ri-phone-line",
};
